import React from 'react'
import { Award, Shield, CheckCircle2, MapPin, Check } from 'lucide-react'
import { useLanguage } from '../context/LanguageContext'

export default function About() {
  const { language, t } = useLanguage()
  const values = [
    {
      icon: Award,
      title: t('about.val1_title'),
      description: t('about.val1_desc'),
      color: 'text-[#a78458]',
      bgColor: 'bg-[#a78458]/5'
    },
    {
      icon: Shield,
      title: t('about.val2_title'),
      description: t('about.val2_desc'),
      color: 'text-[#a78458]',
      bgColor: 'bg-[#a78458]/5'
    },
    {
      icon: CheckCircle2,
      title: t('about.val3_title'),
      description: t('about.val3_desc'),
      color: 'text-[#a78458]',
      bgColor: 'bg-[#a78458]/5'
    }
  ]

  return (
    <section id="om-os" className="relative bg-[#FAF8F5] py-16 md:py-20 overflow-hidden">
      <div className="relative z-10 mx-auto max-w-7xl px-6 md:px-12">
        {/* Header */}
        <div className="max-w-3xl mb-10 md:mb-14">
          <h2 className="font-marcellus text-3xl sm:text-4xl md:text-5xl font-light tracking-[0.08em] text-[#2C302E] leading-tight mb-6 uppercase">
            {t('about.title')} <br className="hidden sm:inline" />
            <span className="font-normal text-[#a78458]">{t('about.title_gold')}</span> {t('about.title_end')}
          </h2>
          <p className="text-base sm:text-lg text-[#2C302E]/80 leading-relaxed max-w-2xl">
            {t('about.desc')}
          </p>
        </div>

        {/* Values Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-y-10 md:gap-y-0 md:gap-x-12 mb-12 md:mb-16 divide-y md:divide-y-0 md:divide-x divide-[#E6DFD3] border-t border-b md:border-t-0 md:border-b-0 py-8 md:py-0">
          {values.map((val, idx) => {
            const IconComponent = val.icon
            return (
              <div
                key={idx}
                className="pt-8 md:pt-0 md:px-8 first:pt-0 first:pl-0 last:pr-0"
              >
                <div className="text-[#2C302E] mb-6">
                  <IconComponent className="h-8 w-8" />
                </div>
                <h3 className="font-marcellus text-xl font-light text-[#2C302E] mb-4 uppercase tracking-wider">
                  {val.title}
                </h3>
                <p className="text-sm text-[#2C302E]/70 leading-relaxed">
                  {val.description}
                </p>
              </div>
            )
          })}
        </div>

        {/* Coverage Area / Map Section */}
        <div className="relative overflow-hidden bg-[#241E1B] rounded-none border border-[#a78458]/40 p-8 md:p-12 lg:p-16 flex flex-col lg:flex-row items-center gap-12">
          {/* Golden Denmark Outline Background behind everything */}
          <div className="absolute inset-0 w-full h-full pointer-events-none z-0 opacity-45 mix-blend-lighten flex items-center justify-center overflow-hidden">
            <img 
              src="https://assets.auxodev.dk/assets/b84a6f1a-652f-4145-af52-d7d46fab18e5/a4ca91c0-d3a4-4e9e-b8ed-12cd60792b80.png" 
              alt="Danmarkskort outline baggrund" 
              className="w-full h-full object-contain max-w-5xl scale-125 md:scale-135 transition-transform duration-700"
            />
          </div>

          <div className="flex-1 relative z-10">
            <div className="inline-flex items-center gap-2 mb-6">
              <MapPin className="h-4 w-4 text-[#a78458]" />
              <span className="text-xs font-semibold tracking-wider text-[#FAF8F5]/90 uppercase">
                {t('about.coverage_badge')}
              </span>
            </div>
            <h3 className="font-marcellus text-2xl md:text-3xl font-light text-[#FAF8F5] mb-6 uppercase tracking-wider">
              {t('about.coverage_title')}
            </h3>
            <p className="text-sm sm:text-base text-[#FAF8F5]/80 leading-relaxed mb-6">
              {t('about.coverage_desc')}
            </p>
            <ul className="grid grid-cols-2 gap-4 text-sm font-medium text-[#FAF8F5]/90">
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-[#a78458] shrink-0" />
                <span>{t('about.coverage_jylland')}</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-[#a78458] shrink-0" />
                <span>{t('about.coverage_fyn')}</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-[#a78458] shrink-0" />
                <span>{t('about.coverage_sjaelland')}</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-[#a78458] shrink-0" />
                <span>{language === 'da' ? 'Hurtig udrykning' : 'Fast response'}</span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="h-4 w-4 text-[#a78458] shrink-0" />
                <span>{language === 'da' ? 'Lokale teams' : 'Local teams'}</span>
              </li>
            </ul>
          </div>

          {/* Minimalist Map Graphic */}
          <div className="w-full lg:w-96 flex justify-center relative z-10">
            <div className="relative w-72 h-72 bg-[#2A2320] rounded-none flex items-center justify-center border border-[#a78458]/50">
              {/* Abstract Denmark/Jutland-Funen representation */}
              <svg viewBox="0 0 100 100" className="relative z-10 w-48 h-48 text-[#a78458]/45">
                {/* Jutland */}
                <path d="M25,10 L45,10 L40,85 L20,85 Z" fill="currentColor" />
                {/* Funen */}
                <path d="M48,65 L65,65 L60,80 L45,80 Z" fill="currentColor" />
                {/* Zealand */}
                <path d="M68,60 L85,60 L80,80 L65,80 Z" fill="currentColor" />
              </svg>

              {/* Pulsing coverage markers */}
              {/* Fredericia (Base) */}
              <div className="absolute top-[68%] left-[44%] -translate-x-1/2 -translate-y-1/2 z-20">
                <span className="absolute inline-flex h-4 w-4 animate-ping rounded-none bg-[#bfa17a] opacity-75" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-none bg-[#bfa17a]" />
                <span className="absolute left-4 top-1/2 -translate-y-1/2 whitespace-nowrap bg-[#bfa17a] text-[#1C1816] text-[10px] font-semibold tracking-wider uppercase px-2 py-0.5 rounded-none shadow-sm">
                  {language === 'da' ? 'Fredericia (Hovedkontor)' : 'Fredericia (HQ)'}
                </span>
              </div>

              {/* Aarhus */}
              <div className="absolute top-[45%] left-[38%] -translate-x-1/2 -translate-y-1/2">
                <span className="relative inline-flex h-2 w-2 rounded-none bg-[#bfa17a]" />
              </div>

              {/* Odense */}
              <div className="absolute top-[72%] left-[56%] -translate-x-1/2 -translate-y-1/2">
                <span className="relative inline-flex h-2 w-2 rounded-none bg-[#bfa17a]" />
              </div>

              {/* Aalborg */}
              <div className="absolute top-[22%] left-[32%] -translate-x-1/2 -translate-y-1/2">
                <span className="relative inline-flex h-2 w-2 rounded-none bg-[#bfa17a]" />
              </div>

              {/* Esbjerg */}
              <div className="absolute top-[68%] left-[24%] -translate-x-1/2 -translate-y-1/2">
                <span className="relative inline-flex h-2 w-2 rounded-none bg-[#bfa17a]" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
